import { idDeLaReferencia } from './spec.js';
/** Lo que puede cambiar de un punto a otro. Se usan para describir el eje; no deciden equivalencia semántica. */
export const EJES_DE_GRAFICO = ['periodo', 'ambito', 'poblacion', 'mide'];
/** Más de doce barras ya no se leen en una pantalla de móvil; para series largas está la línea. */
export const MAXIMO_DE_BARRAS = 60;
export const esTemporal = (forma) => forma === 'linea' || forma === 'area';
const normalizar = (texto) => (texto ?? '').toLowerCase().replace(/\s+/gu, ' ').trim();
const distintos = (valores) => new Set(valores).size;
/** Los ejes en los que los hechos no coinciden. */
const ejesQueCambian = (hechos) => EJES_DE_GRAFICO.filter(eje => distintos(hechos.map(h => normalizar(h[eje]))) > 1);
/** Integridad del dibujo. El juez evalúa significado, título y pie con las fuentes.
 * No exigir igualdad textual de medidas ni población: GRA-16/18.
 */
export function comprobarGrafico(grafico, hechos) {
    if (grafico.puntos.length < 2 || grafico.puntos.length > 60)
        return [{ tipo: 'puntos_fuera_de_rango' }];
    const motivos = [];
    const ids = grafico.puntos.flatMap(p => (p.hechoX ? [p.hecho, p.hechoX] : [p.hecho])).map(idDeLaReferencia);
    for (const id of new Set(ids)) {
        const h = hechos.get(id);
        if (!h)
            motivos.push({ tipo: 'hecho_desconocido', hecho: id });
        else if (typeof h.valor !== 'number' || !Number.isFinite(h.valor))
            motivos.push({ tipo: 'hecho_sin_valor', hecho: id });
    }
    if (motivos.length)
        return motivos;
    const ys = grafico.puntos.map(p => hechos.get(idDeLaReferencia(p.hecho)));
    const series = grafico.puntos.map(p => normalizar(p.serie));
    const nombres = new Set(series);
    const requiereSeries = ['agrupadas', 'apiladas', 'combinado'].includes(grafico.forma);
    if ((requiereSeries && (nombres.size < 2 || nombres.has(''))) ||
        (nombres.size > 1 && nombres.has('')) ||
        (grafico.forma === 'combinado' && nombres.size !== 2) ||
        (['barras', 'dona'].includes(grafico.forma) && nombres.size > 1))
        motivos.push({ tipo: 'series_invalidas' });
    const posiciones = grafico.puntos.map((p, i) => `${series[i]}|${esTemporal(grafico.forma) ? String(claveDePeriodo(ys[i].periodo)) : normalizar(p.etiqueta)}`);
    if (!esTemporal(grafico.forma) && new Set(posiciones).size !== posiciones.length)
        motivos.push({ tipo: 'etiqueta_repetida' });
    const pares = grafico.puntos.map(p => `${p.hecho}|${p.hechoX ?? ''}`);
    if (new Set(pares).size !== pares.length)
        motivos.push({ tipo: 'hecho_repetido', hecho: idDeLaReferencia(grafico.puntos.find((_, i) => pares.indexOf(pares[i]) !== i).hecho) });
    comprobarUnidades(ys, motivos);
    if (grafico.forma === 'dispersion') {
        if (grafico.puntos.some(p => !p.hechoX))
            motivos.push({ tipo: 'coordenada_x_invalida' });
        else
            comprobarUnidades(grafico.puntos.map(p => hechos.get(idDeLaReferencia(p.hechoX))), motivos);
    }
    else if (grafico.puntos.some(p => p.hechoX))
        motivos.push({ tipo: 'coordenada_x_invalida' });
    if (grafico.forma === 'dona' || grafico.forma === 'apiladas') {
        if (ys.some(h => h.valor < 0))
            motivos.push({ tipo: 'valor_negativo' });
        if (ys.every(h => h.valor === 0))
            motivos.push({ tipo: 'total_invalido' });
        // No normalizar a 100 una encuesta con respuestas múltiples ni un reparto incompleto.
        if (grafico.forma === 'dona' && ys[0].unidad === 'porcentaje' && Math.abs(ys.reduce((n, h) => n + h.valor, 0) - 100) > 1)
            motivos.push({ tipo: 'total_invalido' });
    }
    // Con datos de más de un estudio, el pie es lo que avisa de que no preguntaron lo mismo a la misma gente: sin él, el
    // dibujo los junta sin decirlo. Es opcional en la spec, así que lo exige el motor (GRA-21, comprobación en producción de GRA-02).
    if (distintos(ids.map(id => hechos.get(id).fuenteSlug)) > 1 && !grafico.pie?.trim())
        motivos.push({ tipo: 'pie_obligatorio' });
    if (esTemporal(grafico.forma)) {
        const periodos = ys.map(h => leerPeriodo(h.periodo));
        if (periodos.some(p => p === null) || new Set(periodos.map(p => p?.granularidad)).size !== 1 || new Set(posiciones).size !== posiciones.length)
            motivos.push({ tipo: 'periodos_no_comparables' });
    }
    return motivos;
}
function comprobarUnidades(hechos, motivos) {
    if (hechos.some(h => !normalizar(h.unidad)))
        motivos.push({ tipo: 'sin_unidad' });
    else {
        const dinero = hechos.some(h => h.unidad === 'moneda');
        if (distintos(hechos.map(h => normalizar(h.unidad))) > 1 || (dinero && distintos(hechos.map(h => normalizar(h.divisa))) > 1))
            motivos.push({ tipo: 'unidad_distinta' });
        if (dinero && hechos.some(h => !normalizar(h.divisa)))
            motivos.push({ tipo: 'moneda_sin_divisa' });
    }
}
/** Nombres de mes que se reconocen, enteros o abreviados (inglés y español), y su número. */
const MES_POR_NOMBRE = Object.fromEntries([
    ['jan january ene enero', 1],
    ['feb february febrero', 2],
    ['mar march marzo', 3],
    ['apr april abr abril', 4],
    ['may mayo', 5],
    ['jun june junio', 6],
    ['jul july julio', 7],
    ['aug august ago agosto', 8],
    ['sep sept september septiembre setiembre', 9],
    ['oct october octubre', 10],
    ['nov november noviembre', 11],
    ['dec december dic diciembre', 12],
].flatMap(([nombres, numero]) => nombres.split(' ').map(n => [n, numero])));
/**
 * Lee un periodo («2025», «FY2024», «Q2 2025», «H2 2025», «Jan 2025», «2025/2026»): su granularidad y su
 * posición en el tiempo, como un año con fracción (2025,25 es el segundo trimestre de 2025). `null` si no
 * se puede leer sin adivinar: sin año, un rango de meses o trimestres («December 2025 - January 2026»,
 * «Q4 2025 - Q1 2026») o palabras que no son un periodo («spring 2025»).
 *
 * Una serie temporal solo es una serie si todos sus periodos se leen y tienen **la misma granularidad**:
 * un dato anual junto a uno trimestral de la misma medida no se comparan.
 */
export function leerPeriodo(periodo) {
    if (periodo === null || periodo === undefined)
        return null;
    const t = periodo.trim().toLowerCase();
    const anios = t.match(/(?<!\d)(?:19|20)\d{2}(?!\d)/gu) ?? [];
    if (anios.length === 0)
        return null;
    const anio = Number(anios[0]);
    const trimestre = /(?<![a-z0-9])q([1-4])(?![a-z0-9])/u.exec(t) ?? /(?<![a-z0-9])([1-4])q(?![a-z0-9])/u.exec(t);
    const semestre = /(?<![a-z0-9])h([12])(?![a-z0-9])/u.exec(t) ?? /(?<![a-z0-9])([12])h(?![a-z0-9])/u.exec(t);
    const meses = new Set([...t.matchAll(/(?<![a-z])([a-z]+)\.?(?![a-z])/gu)].map(m => MES_POR_NOMBRE[m[1]]).filter((m) => m !== undefined));
    if (trimestre !== null)
        return anios.length > 1 ? null : { granularidad: 'trimestre', posicion: anio + (Number(trimestre[1]) - 1) / 4 };
    if (semestre !== null)
        return anios.length > 1 ? null : { granularidad: 'semestre', posicion: anio + (Number(semestre[1]) - 1) / 2 };
    if (meses.size > 0) {
        if (meses.size > 1 || anios.length > 1)
            return null;
        const numero = [...meses][0];
        return { granularidad: 'mes', posicion: anio + (numero - 1) / 12 };
    }
    // Solo un año («2025», «FY2024», «2025/2026», «2025/26»): cualquier otra palabra no es un periodo que sepamos ordenar.
    return /^(?:fy\s?)?(?:19|20)\d{2}(?:\s*[/–-]\s*(?:(?:19|20)\d{2}|\d{2}))?$/u.test(t) ? { granularidad: 'anio', posicion: anio } : null;
}
/** La posición de un periodo en el tiempo (año con fracción) para ordenar; `null` si no se puede leer. */
export function claveDePeriodo(periodo) {
    return leerPeriodo(periodo)?.posicion ?? null;
}
/**
 * Los datos que se dibujan: **cada valor sale de su hecho**, no del bloque. `null` si el gráfico no
 * pasa `comprobarGrafico`; quien lo pinta enseña entonces solo el texto.
 *
 * **Con el periodo como eje, la etiqueta es el periodo del hecho** («Q2 2025», «2019») y no la del
 * modelo: una etiqueta de año equivocada, con la cifra bien, es un gráfico falso que ninguna
 * comprobación de comparabilidad ve (revisión adversarial de GRA-01). Además los puntos se ordenan
 * en el tiempo, así que el modelo tampoco tiene que acertar el orden. Con cualquier otro eje, la
 * opción de respuesta o el grupo solo los sabe el modelo: se respetan su etiqueta y su orden (en un
 * reparto suele haber uno que no es el del tamaño de la barra, por ejemplo de menor a mayor empresa)
 * y el juez lee cada etiqueta junto a la cifra de su fuente.
 */
export function resolverGrafico(grafico, hechos) {
    if (comprobarGrafico(grafico, hechos).length > 0)
        return null;
    const deLosPuntos = grafico.puntos.map(p => hechos.get(idDeLaReferencia(p.hecho)));
    const cambios = ejesQueCambian(deLosPuntos);
    const eje = esTemporal(grafico.forma) ? 'periodo' : cambios.length === 1 ? cambios[0] : 'mide';
    const puntos = grafico.puntos.map((p, i) => {
        const hecho = deLosPuntos[i];
        return {
            etiqueta: esTemporal(grafico.forma) ? (hecho.periodo ?? p.etiqueta) : p.etiqueta,
            etiquetaDelModelo: p.etiqueta,
            valor: hecho.valor,
            cifra: hecho.cifra,
            hechoId: hecho.hechoId,
            fuenteSlug: hecho.fuenteSlug,
            pagina: hecho.pagina,
            periodo: hecho.periodo ?? null,
            aproximado: hecho.aproximado === true,
            serie: p.serie ?? '',
            x: p.hechoX
                ? (() => {
                    const h = hechos.get(idDeLaReferencia(p.hechoX));
                    return { hechoId: h.hechoId, valor: h.valor, cifra: h.cifra, unidad: h.unidad, divisa: h.divisa ?? null };
                })()
                : null,
        };
    });
    if (esTemporal(grafico.forma)) {
        const claves = puntos.map(p => claveDePeriodo(p.periodo));
        if (claves.every((c) => c !== null)) {
            const orden = puntos.map((p, i) => ({ p, clave: claves[i] })).sort((a, b) => a.clave - b.clave);
            puntos.splice(0, puntos.length, ...orden.map(o => o.p));
        }
    }
    const primero = deLosPuntos[0];
    return {
        forma: grafico.forma,
        titulo: grafico.titulo,
        eje,
        unidad: primero.unidad,
        divisa: primero.divisa ?? null,
        puntos,
    };
}
//# sourceMappingURL=grafico.js.map